import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaExclamationCircle } from "react-icons/fa";
import { LastChangedTag } from "../../../../../common/editing/LastChangedTag/LastChangedTag";
import { useGetSourcingEventReadinessQuery, type SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { isFetchBaseQueryError } from "../../../../../common/errors/isFetchBaseQueryError";
import { isApiErrorDto } from "../../../../../common/errors/isApiErrorDto";
import {
  CONTRACT_NATURE_LABELS,
  FRAMEWORK_AGREEMENT_LABELS,
  PROCEDURE_TYPE_LABELS,
  RESERVED_EXECUTION_LABELS,
  RESERVED_PARTICIPATION_LABELS,
} from "../utils/publicProcurementOptions";
import { formatContractDuration, formatContractOptions, formatFrameworkAgreement } from "../utils/displayFormatters";
import { StepNavigation } from "../../../../../common/StepNavigation";

const translateLabels = (labels: Record<string, string>, t: (key: string) => string): Record<string, string> =>
  Object.fromEntries(Object.entries(labels).map(([k, v]) => [k, t(v)]));

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  previousStep: () => void;
  navigateToInfo: () => void;
  navigateToProcedure: () => void;
  isActive: boolean;
}

interface ValidationItem {
  label: string;
  isValid: boolean;
  step: "info" | "procedure";
}

const WIZARD_FIELDS: Record<string, { label: string; step: "info" | "procedure" }> = {
  TitlePresent: { label: "Title", step: "info" },
  DescriptionPresent: { label: "Description", step: "info" },
  MainCpvCodeSet: { label: "CPV code", step: "info" },
  ContractNatureSet: { label: "Contract type", step: "info" },
  ProcedureTypeSet: { label: "Procedure type", step: "procedure" },
  TenderValidityMonthsSet: { label: "Tender validity period", step: "procedure" },
};

export const SummaryStep = ({ eventId, event, previousStep, navigateToInfo, navigateToProcedure, isActive }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();

  const handlePublish = useCallback(() => {
    toast({
      title: `${t("Coming soon")}: ${t("Publish to Doffin")}`,
      status: "info",
      duration: 3000,
    });
  }, [toast, t]);

  const { data: readinessData, isLoading: readinessLoading, isError: readinessError, error: readinessErrorData, refetch } =
    useGetSourcingEventReadinessQuery({ eventId });

  useEffect(() => {
    if (isActive) refetch();
  }, [isActive, refetch]);

  const validationItems: ValidationItem[] = readinessData
    ? readinessData.fields
        .filter((f) => f.gate === "PUBLISH" && f.fieldKey in WIZARD_FIELDS)
        .map((f) => ({
          label: t(WIZARD_FIELDS[f.fieldKey].label),
          isValid: f.satisfied,
          step: WIZARD_FIELDS[f.fieldKey].step,
        }))
    : [];

  const completedCount = validationItems.filter((i) => i.isValid).length;
  const allComplete = validationItems.length > 0 && completedCount === validationItems.length;

  const navigateToStep = (step: "info" | "procedure") => {
    if (step === "info") navigateToInfo();
    else navigateToProcedure();
  };

  const contractNatureLabels = translateLabels(CONTRACT_NATURE_LABELS, t);
  const procedureTypeLabels = translateLabels(PROCEDURE_TYPE_LABELS, t);
  const frameworkAgreementLabels = translateLabels(FRAMEWORK_AGREEMENT_LABELS, t);
  const reservedParticipationLabels = translateLabels(RESERVED_PARTICIPATION_LABELS, t);
  const reservedExecutionLabels = translateLabels(RESERVED_EXECUTION_LABELS, t);

  const { publicContext, eventData } = event;

  const contractDurationDisplay = formatContractDuration(eventData.contractDuration, t);
  const contractOptionsDisplay = formatContractOptions(eventData.options, t);
  const frameworkDisplay = formatFrameworkAgreement(publicContext.frameworkAgreement, frameworkAgreementLabels, t);

  return (
    <Box pb="8" pt="10">
      {/* Validation Checklist */}
      {readinessError ? (
        <Alert status="error" borderRadius="lg" mb="8">
          <AlertIcon />
          <Text fontSize="sm">
            {readinessErrorData && isFetchBaseQueryError(readinessErrorData) && isApiErrorDto(readinessErrorData.data)
              ? t(readinessErrorData.data.errorCode)
              : t("Something went wrong!")}
          </Text>
        </Alert>
      ) : (
        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderColor={allComplete ? "green.200" : "orange.200"}
          bg={allComplete ? "green.50" : "orange.50"}
          p="5"
          mb="8"
        >
          <Flex align="center" justify="space-between" mb="3">
            <Text fontWeight="bold" fontSize="sm">
              {t("Checklist")}
            </Text>
            {readinessLoading ? (
              <Spinner size="xs" />
            ) : (
              <Text fontSize="sm" color="smMuted">
                {completedCount}/{validationItems.length} {t("completed")}
              </Text>
            )}
          </Flex>
          {readinessLoading ? (
            <Flex justify="center" py="4">
              <Spinner size="sm" />
            </Flex>
          ) : (
            <List spacing="2">
              {validationItems.map((item) => (
                <ListItem
                  key={item.label}
                  fontSize="sm"
                  display="flex"
                  alignItems="center"
                  cursor={!item.isValid ? "pointer" : "default"}
                  onClick={() => {
                    if (!item.isValid) navigateToStep(item.step);
                  }}
                  _hover={!item.isValid ? { textDecoration: "underline" } : {}}
                >
                  <ListIcon
                    as={item.isValid ? FaCheck : FaExclamationCircle}
                    color={item.isValid ? "smSuccess" : "smWarning"}
                  />
                  <Text color={item.isValid ? "smText" : "smWarning"}>
                    {item.label}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Info Summary */}
      <Box mb="6">
        <Flex align="center" justify="space-between" mb="3">
          <Heading as="h3" size="sm">
            {t("Basic information")}
          </Heading>
          <Button variant="link" colorScheme="teal" size="sm" onClick={navigateToInfo}>
            {t("Edit")}
          </Button>
        </Flex>
        <Box borderWidth="1px" borderRadius="lg" borderColor="smBorder" p="4">
          <SummaryRow label={t("Title")} value={eventData.title || "-"} />
          <Divider my="2" />
          <SummaryRow
            label={t("Description")}
            value={eventData.description ? t("Provided") : "-"}
          />
          <Divider my="2" />
          <SummaryRow label={t("CPV code")} value={publicContext.cpvClassification?.mainCode ?? "-"} />
          <Divider my="2" />
          <SummaryRow
            label={t("Estimated value")}
            value={
              publicContext.estimatedValue?.value !== null && publicContext.estimatedValue?.value !== undefined
                ? `${publicContext.estimatedValue.value.toLocaleString("nb-NO")} ${t("NOK")}`
                : "-"
            }
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Contract type")}
            value={
              publicContext.contractNature
                ? contractNatureLabels[publicContext.contractNature] ?? publicContext.contractNature
                : "-"
            }
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Contract duration")}
            value={contractDurationDisplay}
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Contract options")}
            value={contractOptionsDisplay}
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Reserved participation")}
            value={
              publicContext.reservedParticipation && publicContext.reservedParticipation !== "NONE"
                ? reservedParticipationLabels[publicContext.reservedParticipation] ?? publicContext.reservedParticipation
                : "-"
            }
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Reserved execution")}
            value={
              publicContext.reservedExecution && publicContext.reservedExecution !== "NONE"
                ? reservedExecutionLabels[publicContext.reservedExecution] ?? publicContext.reservedExecution
                : "-"
            }
          />
        </Box>
      </Box>

      {/* Procedure Summary */}
      <Box mb="6">
        <Flex align="center" justify="space-between" mb="3">
          <Heading as="h3" size="sm">
            {t("Procedure")}
          </Heading>
          <Button variant="link" colorScheme="teal" size="sm" onClick={navigateToProcedure}>
            {t("Edit")}
          </Button>
        </Flex>
        <Box borderWidth="1px" borderRadius="lg" borderColor="smBorder" p="4">
          <SummaryRow
            label={t("Procedure type")}
            value={
              publicContext.procedureType
                ? procedureTypeLabels[publicContext.procedureType] ?? publicContext.procedureType
                : "-"
            }
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Tender validity period")}
            value={
              publicContext.eFormsNoticeData?.tenderValidityMonths
                ? `${publicContext.eFormsNoticeData.tenderValidityMonths} ${t("month", { count: publicContext.eFormsNoticeData.tenderValidityMonths })}`
                : "-"
            }
          />
          <Divider my="2" />
          <SummaryRow
            label={t("Framework agreement")}
            value={frameworkDisplay}
          />
        </Box>
      </Box>

      {/* Last Saved */}
      <Flex justify="center" mb="6">
        <LastChangedTag timestamp={event.modifiedAt} />
      </Flex>

      {/* Actions */}
      <StepNavigation
        onPrevious={previousStep}
        onNext={handlePublish}
        nextLabel={t("Publish to Doffin")}
        nextIsAction
      />
    </Box>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Flex justify="space-between" align="start" py="1">
    <Text fontSize="sm" color="smMuted" flexShrink={0} mr="4">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium" textAlign="right" noOfLines={2}>
      {value}
    </Text>
  </Flex>
);
